/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IMoeV2TableData } from './moe-v2-table.data';
import { IMoeV2TableProps } from './moe-v2-table.props.autogenerated';

// Rich text content interface for D365 Commerce
export interface IRichTextContent {
    nodeType: string;
    childNodes?: IRichTextContent[];
    text?: string;
    [key: string]: any;
}

// Header cell configuration with rich text
export interface IHeaderCell {
    content?: IRichTextContent;
    colspan?: number;
    rowspan?: number;
    backgroundColor?: string;
    textAlign?: 'left' | 'center' | 'right';
    width?: string;
    verticalAlign?: 'top' | 'middle' | 'bottom';
}

// Table cell configuration with rich text
export interface ITableCell {
    content?: IRichTextContent;
    colspan?: number;
    rowspan?: number;
    backgroundColor?: string;
    textAlign?: 'left' | 'center' | 'right';
    width?: string;
    verticalAlign?: 'top' | 'middle' | 'bottom';
}

// Table row configuration
export interface ITableRow {
    cells?: ITableCell[];
}

// Internal interfaces for component state (not in autogenerated file)
export interface ICellPosition {
    rowIndex: number;
    cellIndex: number;
}

export interface ITableState {
    selectedCells: ICellPosition[];
    editingCell: ICellPosition | null;
    contextMenu: {
        x: number;
        y: number;
        position: ICellPosition;
    } | null;
}

export interface IMoeV2TableViewProps extends IMoeV2TableProps<IMoeV2TableData> {
    // Extended props for view layer
    isEditMode: boolean;
    tableState: ITableState;
    editingContent: string;
    processedHeaders: IHeaderCell[];
    processedRows: ITableRow[];

    // Event handlers
    handleCellClick: (position: ICellPosition, event: React.MouseEvent) => void;
    handleCellDoubleClick: (position: ICellPosition) => void;
    handleEditComplete: (save?: boolean) => void;
    handleContextMenu: (event: React.MouseEvent, position: ICellPosition) => void;
    handleKeyDown: (event: React.KeyboardEvent) => void;
    closeContextMenu: () => void;
    updateEditingContent: (content: string) => void;

    // Table management functions
    addRow: () => void;
    deleteRow: () => void;
    addColumn: () => void;
    deleteColumn: () => void;
    mergeCells: () => void;
    splitCell: () => void;
    handleCopyPaste: () => void;
}

/**
 * MoeV2Table component
 * @extends {React.PureComponent<IMoeV2TableProps<IMoeV2TableData>>}
 */
class MoeV2Table extends React.PureComponent<IMoeV2TableProps<IMoeV2TableData>, ITableState> {
    private editingContent: string = '';

    constructor(props: IMoeV2TableProps<IMoeV2TableData>) {
        super(props);

        // Initialize state
        this.state = {
            selectedCells: [],
            editingCell: null,
            contextMenu: null
        };
    }

    // Check if in edit mode (Site Builder)
    private get isEditMode(): boolean {
        return (
            this.props.context?.request?.user?.isAuthenticated &&
            this.props.context?.actionContext?.requestContext?.url?.requestUrl?.pathname?.includes('_authoring')
        );
    }

    // Helper function to create default rich text content
    private createDefaultRichText(text: string): IRichTextContent {
        return {
            nodeType: 'root',
            childNodes: [
                {
                    nodeType: 'paragraph',
                    childNodes: [
                        {
                            nodeType: 'text',
                            text: text
                        }
                    ]
                }
            ]
        };
    }

    // Get processed headers with defaults
    private get processedHeaders(): IHeaderCell[] {
        const configHeaders = this.props.config.headers as any[];

        if (!configHeaders || configHeaders.length === 0) {
            return [
                { content: this.createDefaultRichText('Header 1'), colspan: 1, rowspan: 1 },
                { content: this.createDefaultRichText('Header 2'), colspan: 1, rowspan: 1 },
                { content: this.createDefaultRichText('Header 3'), colspan: 1, rowspan: 1 }
            ];
        }

        return configHeaders.map(header => ({
            content: header.content || this.createDefaultRichText('Header'),
            colspan: header.colspan || 1,
            rowspan: header.rowspan || 1,
            backgroundColor: header.backgroundColor || '#f8f9fa',
            textAlign: header.textAlign,
            width: header.width || '0',
            verticalAlign: header.verticalAlign || 'top'
        }));
    }

    // Get processed rows with defaults
    private get processedRows(): ITableRow[] {
        const configRows = this.props.config.rows as any[];

        if (!configRows || configRows.length === 0) {
            return [
                {
                    cells: [
                        { content: this.createDefaultRichText('Cell 1'), colspan: 1, rowspan: 1 },
                        { content: this.createDefaultRichText('Cell 2'), colspan: 1, rowspan: 1 },
                        { content: this.createDefaultRichText('Cell 3'), colspan: 1, rowspan: 1 }
                    ]
                }
            ];
        }

        return configRows.map(row => ({
            cells: row.cells
                ? row.cells.map((cell: any) => ({
                      content: cell.content || this.createDefaultRichText('Cell'),
                      colspan: cell.colspan || 1,
                      rowspan: cell.rowspan || 1,
                      backgroundColor: cell.backgroundColor || '#ffffff',
                      textAlign: cell.textAlign,
                      width: cell.width || 0,
                      verticalAlign: cell.verticalAlign || 'top'
                  }))
                : []
        }));
    }

    // Handle cell click for selection
    private handleCellClick = (position: ICellPosition, event: React.MouseEvent): void => {
        if (!this.isEditMode) {
            return;
        }
        event.preventDefault();

        if (event.ctrlKey || event.metaKey) {
            // Multi-select
            const exists = this.state.selectedCells.some(
                (p: ICellPosition) => p.rowIndex === position.rowIndex && p.cellIndex === position.cellIndex
            );

            this.setState({
                selectedCells: exists
                    ? this.state.selectedCells.filter(
                          (p: ICellPosition) => !(p.rowIndex === position.rowIndex && p.cellIndex === position.cellIndex)
                      )
                    : [...this.state.selectedCells, position],
                editingCell: null
            });
        } else {
            // Single select
            this.setState({
                selectedCells: [position],
                editingCell: null
            });
        }
    };

    // Handle cell double-click for editing
    private handleCellDoubleClick = (position: ICellPosition): void => {
        if (!this.isEditMode) {
            return;
        }

        // Note: In real implementation, this would open Site Builder's rich text editor
        // For now, we'll use a simple text editor
        let cellContent = '';

        if (position.rowIndex === -1) {
            // Header cell
            const headerCell = this.processedHeaders[position.cellIndex];
            cellContent = this.extractTextFromRichText(headerCell?.content);
        } else {
            // Data cell
            const rowCell = this.processedRows[position.rowIndex]?.cells?.[position.cellIndex];
            cellContent = this.extractTextFromRichText(rowCell?.content);
        }

        this.editingContent = cellContent;
        this.setState({
            editingCell: position,
            selectedCells: [position]
        });
    };

    // Extract text from rich text content for editing
    private extractTextFromRichText(richText?: IRichTextContent): string {
        if (!richText) {
            return '';
        }

        const extractText = (node: IRichTextContent): string => {
            if (node.text) {
                return node.text;
            }
            if (node.childNodes) {
                return node.childNodes.map(child => extractText(child)).join('');
            }
            return '';
        };

        return extractText(richText);
    }

    // Handle editing completion
    private handleEditComplete = (save: boolean = true): void => {
        if (!this.state.editingCell) {
            return;
        }

        if (save) {
            // In a real implementation, this would update the config with rich text
            // For demo purposes, we'll log the content
            console.log('Saving cell content:', this.editingContent);
            console.log('Position:', this.state.editingCell);
        }

        this.setState({ editingCell: null });
        this.editingContent = '';
    };

    // Handle context menu
    private handleContextMenu = (event: React.MouseEvent, position: ICellPosition): void => {
        if (!this.isEditMode) {
            return;
        }
        event.preventDefault();

        this.setState({
            contextMenu: {
                x: event.clientX,
                y: event.clientY,
                position
            }
        });
    };

    // Handle keyboard events
    private handleKeyDown = (event: React.KeyboardEvent): void => {
        if (!this.isEditMode) {
            return;
        }

        if (this.state.editingCell) {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                this.handleEditComplete(true);
            } else if (event.key === 'Escape') {
                event.preventDefault();
                this.handleEditComplete(false);
            }
            return;
        }

        if (this.state.selectedCells.length > 0) {
            if (event.key === 'Delete' || event.key === 'Backspace') {
                event.preventDefault();
                console.log('Clear selected cells');
            } else if (event.key === 'Enter') {
                event.preventDefault();
                this.handleCellDoubleClick(this.state.selectedCells[0]);
            }
        }
    };

    // Table management functions
    private addRow = (): void => {
        console.log('Add row');
        // In real implementation, this would add a new row to config.rows
    };

    private deleteRow = (): void => {
        if (this.state.selectedCells.length === 0) {
            return;
        }
        console.log('Delete row:', this.state.selectedCells[0].rowIndex);
        this.setState({ selectedCells: [] });
    };

    private addColumn = (): void => {
        console.log('Add column');
        // In real implementation, this would add a new cell to each row and header
    };

    private deleteColumn = (): void => {
        if (this.state.selectedCells.length === 0) {
            return;
        }
        console.log('Delete column:', this.state.selectedCells[0].cellIndex);
        this.setState({ selectedCells: [] });
    };

    private mergeCells = (): void => {
        if (this.state.selectedCells.length < 2) {
            return;
        }
        console.log('Merge cells:', this.state.selectedCells);
        this.setState({ selectedCells: [], contextMenu: null });
    };

    private splitCell = (): void => {
        if (this.state.selectedCells.length !== 1) {
            return;
        }
        console.log('Split cell:', this.state.selectedCells[0]);
        this.setState({ contextMenu: null });
    };

    private handleCopyPaste = (): void => {
        console.log('Handle copy/paste');
    };

    private closeContextMenu = (): void => {
        this.setState({ contextMenu: null });
    };

    private updateEditingContent = (content: string): void => {
        this.editingContent = content;
    };

    public render(): JSX.Element | null {
        // Prepare enhanced props for view
        const viewProps: IMoeV2TableViewProps = {
            ...this.props,
            isEditMode: this.isEditMode,
            tableState: this.state,
            editingContent: this.editingContent,
            processedHeaders: this.processedHeaders,
            processedRows: this.processedRows,

            // Event handlers
            handleCellClick: this.handleCellClick,
            handleCellDoubleClick: this.handleCellDoubleClick,
            handleEditComplete: this.handleEditComplete,
            handleContextMenu: this.handleContextMenu,
            handleKeyDown: this.handleKeyDown,
            closeContextMenu: this.closeContextMenu,
            updateEditingContent: this.updateEditingContent,

            // Table management functions
            addRow: this.addRow,
            deleteRow: this.deleteRow,
            addColumn: this.addColumn,
            deleteColumn: this.deleteColumn,
            mergeCells: this.mergeCells,
            splitCell: this.splitCell,
            handleCopyPaste: this.handleCopyPaste
        };

        return this.props.renderView(viewProps);
    }
}

export default MoeV2Table;
